import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { withForbidden } from '../../../../../global/components/HOCs/with-forbidden';
import { withLayout } from '../../../../../global/components/HOCs/with-layout';
import { withScrollTop } from '../../../../../global/components/HOCs/with-scroll-Top';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { TcsmFeatures } from '../../../../../global/models/features-model';
import AccountInfoContainer from '../../../../account-info/components/account-info-container';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { DependentInfo } from '../../../../account-info/features/dependents/dependent-info';
import { MaritalStatusApp } from '../../../../account-info/features/marital-status/marital-status';
import { PersonalInformationApp } from '../../../../account-info/features/personal-info/personal-info';
import { MyRelationshipsApp } from '../../../../account-info/features/relationships/my-relationships';
import { useAccountInfoSettings } from '../../../../account-info/hooks/useAccountInfoSettings';
import { NewHireEnrollmentRoutes } from '../models/new-hire-routes';
import { PensionEnrollment } from './pension/pension-enrollment';
import { CoverageEnrollment } from './coverage/coverage-enrollment';
import { useDemographicsPerson } from '../../../../account-info/hooks/useDemographicsPerson';
import { DemographicsContext } from '../../../../../global/models/demographics-model';
import { setCurrentNewHireBenefitEnrollmentStep, setCurrentNewHireEnrollmentStep } from '../slice/new-hire-enrollment-slices';
import { NewHireEnrollmentStep } from '../models/new-hire-enrollment-models';
import { useAnnualEnrollmentSettings } from '../../../../annual-enrollment/hooks/useAnnualEnrollmentSettings';
import { TcsmAppConfigs } from '../../../../../global/models/configuration-models';
import { useSpouseInformation } from '../../../../account-info/hooks/useSpouseInfo';
import { useDependents } from '../../../../account-info/hooks/useDependents';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { Person } from '../../../../../global/models/person-model';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../../annual-enrollment/models/annual-enrollment-models';
import GenericStepper from '../../../../../global/components/navigation/generic-stepper';
import { useBenefitEnrollmentStep } from '../hooks/useBenefitEnrollmentStep';
import { getBenefitEnrollmentStepItems, getBenefitEnrollmentSteps } from './helpers/coverage-display-helpers';
import { NewHireProps } from '../../../models/arc-epi-props';
import { NewHireMemberEnrollmentPersonalInfoWizard } from '../member/wizards/new-hire-member-enrollment-wizard';
import { useCurrentEmployerClientIdHook } from '../hooks/useCurrentEmployerId';
import { useOnlyOtpEmployment } from '../hooks/useNewhireEnrollmentAllowed';
import { useCompensationState } from '../hooks/useCompensationState';
import { ConfirmationStatusCode } from '../models/new-hire-confirmation-models';
import { NewHireEventType } from '../../../constants/constant-strings';
import DomesticPartner from '../../../../account-info/features/domestic-partner/domestic-partner';



const RelationshipsWrapper = withScrollTop()(withLayout(null, GpsType.NEW_HIRE_RELATIONSHIP)(withForbidden(TcsmFeatures.Relationships)(AccountInfoContainer)));
const PersonalInfoWrapper = withScrollTop()(withLayout(null, GpsType.NEW_HIRE_PERSONAL_INFO)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer)));
const MaritalStatusWrapper = withLayout(null, GpsType.NEW_HIRE_MARITAL_STATUS)(withForbidden(TcsmFeatures.MaritalStatus)(AccountInfoContainer));
const DependentsWrapper = withLayout(null, GpsType.NEW_HIRE_DEPENDENT)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));

interface Props {
	settings: TcsmAppConfigs;
	eventType?: string;
	confirmationCode?: ConfirmationStatusCode;
	qualifyingEventDateOverwrite?: string;
}

export function BenefitEnrollment(props: Props) {

	const { settings, eventType, confirmationCode, qualifyingEventDateOverwrite } = props;
	const app = useContext(DemographicsContext);
	const routeMatch = useRouteMatch();
	const demographicsSettingsLoaded = useAccountInfoSettings(settings.personalInfoSettingId, settings.relationshipsSettingId);
	const aeSettingsLoaded = useAnnualEnrollmentSettings(settings.annualEnrollmentSettingsId);
	const personLoaded = useDemographicsPerson(app);
	const currentPerson = useSelector<TcsmStates, Person>(state => state?.personalInfo[app]?.currentPerson);
	const spousesLoaded = useSpouseInformation(currentPerson?.person_guid);
	const dependentsLoaded = useDependents(currentPerson?.person_guid);
	const dispatch = useDispatch();
	const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);
	const epiApiState = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const history = useHistory();
	useCompensationState();
	const hasOtpOnly = useOnlyOtpEmployment();

	useEffect(() => {
		dispatch(setCurrentNewHireEnrollmentStep(NewHireEnrollmentStep.BENEFIT_ENROLLMENT));
	}, []);

	if (!demographicsSettingsLoaded || !personLoaded || !aeSettingsLoaded || !spousesLoaded || !dependentsLoaded) {
		return <LoadingCircle />;
	}


	const wizard = new NewHireMemberEnrollmentPersonalInfoWizard(epiApiState, AnnualEnrollmentKey.newHireEnrollment, app, dispatch, history);

	return (
		<>
			<AnnualEnrollmentContext.Provider value={AnnualEnrollmentKey.newHireEnrollment}>
				<GenericStepper
					basePath={routeMatch.path}
					allSteps={getBenefitEnrollmentSteps()}
					useCurrentStep={useBenefitEnrollmentStep}
					setCurrentStep={setCurrentNewHireBenefitEnrollmentStep}
					progressItems={getBenefitEnrollmentStepItems(newHireProps, hasOtpOnly)}
				/>
				<Switch>
					<Route path={`${routeMatch.path}${AccountInfoRoutes.PersonalInfoSegment}`} render={() => (
						<PersonalInfoWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<PersonalInformationApp basePath={routeMatch.path} customPersonalInfoWizard={wizard} />
						</PersonalInfoWrapper>
					)} />
					<Route exact path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} render={() => (
						<RelationshipsWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<MyRelationshipsApp basePath={routeMatch.path} setNextStepCoverage={hasOtpOnly} />
						</RelationshipsWrapper>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.MaritalStatusSegment}`} render={() => (
						<MaritalStatusWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<MaritalStatusApp basePath={routeMatch.path} />
						</MaritalStatusWrapper>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DependentSegment}`} render={() => (
						<DependentsWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<DependentInfo relationshipsPageRoute={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} />
						</DependentsWrapper>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DomesticPartner}`} render={() => (
						<>
							<DomesticPartner basePath={routeMatch.path} />
						</>
					)} />
					<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.Pension}`} render={() => <PensionEnrollment basePath={routeMatch.path} />} />
					<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.Coverage}`} render={() =>
						<CoverageEnrollment
							basePath={routeMatch.path}
							queryString='employer_id'
							getEmployerIdHook={useCurrentEmployerClientIdHook}
							eventType={eventType}
							confirmationCode={confirmationCode}
							qualifyingEventDateOverwrite={qualifyingEventDateOverwrite}
						/>} />

				</Switch>
			</AnnualEnrollmentContext.Provider >

		</>
	);
}

BenefitEnrollment.defaultProps = {
	confirmationCode: null,
	eventType: NewHireEventType,
	qualifyingEventDateOverwrite: null,
};